/*
 * Copyright © 2015-2018 Serenova, LLC. All rights reserved.
 */
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/mapTo';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/catch';

import { fromPromise } from 'rxjs/observable/fromPromise';
import { Toast } from 'cx-ui-components';
import { zip } from 'rxjs/observable/zip';
import { from } from 'rxjs/observable/from';
import { of } from 'rxjs/observable/of';

// import { handleSuccess, handleError } from '../../entities/handleResult';
import { sdkPromise, sdkCall, errorLabel } from '../../../../utils/sdk';

import {
  startReportingSubscriptions,
  reportingSubscriptionStarted,
  requestingSessionInitializationCompleted,
  interactionMonitoringSubscriptionAdded,
  clearMonitorStates,
} from './index';
import { selectSessionId } from './selectors';

const handleError = (error, state) => {
  Toast.error(errorLabel(error));
  localStorage.setItem('SupervisorToolbar', JSON.stringify(state.get('SupervisorToolbar').toJS()));
  sdkCall({ module: 'session', command: 'clearMonitoredMessagingInteractions' });
  return of(clearMonitorStates());
};

export const GetUserExtensions = (action$) =>
  action$
    .ofType('GET_USER_EXTENSIONS')
    .map((a) => {
      a.sdkCall = {
        module: 'session',
        command: 'getUserConfig',
        topic: 'cxengage/session/get-user-config-response',
      };
      return { ...a };
    })
    .concatMap((a) =>
      fromPromise(sdkPromise(a.sdkCall)).map(({ result }) => ({
        type: 'SET_EXTENSION_LIST',
        extensions: result.extensions,
      }))
    );

export const StartBatchRequest = (action$, store) =>
  action$.ofType('START_REPORTING_BATCH_REQUEST_$').switchMap((action) =>
    fromPromise(
      sdkPromise({
        module: 'reporting',
        command: `addStatSubscription`,
        data: {
          statistic: 'interactions-in-conversation-list',
          statId: 'interactions-in-conversation-list',
        },
        topic: `cxengage/reporting/stat-subscription-added`,
      })
    ).mapTo(startReportingSubscriptions())
  );

export const StartBatchSubscription = (action$, store) =>
  action$.ofType('START_REPORTING_SUBSCRIPTION_$').switchMap((action) =>
    fromPromise(
      sdkPromise({
        module: 'subscribe',
        command: 'cxengage/reporting/batch-response',
        topic: 'cxengage/reporting/batch-response',
      })
    ).mapTo(reportingSubscriptionStarted())
  );

export const StartMonitoringSubscriptions = (action$, store) =>
  action$
    .ofType('START_REPORTING_SUBSCRIPTION_$')
    .debounceTime(400)
    .mergeMap((action) =>
      from([
        'cxengage/session/started',
        'cxengage/interactions/messaging/silent-monitor-start',
        'cxengage/interactions/messaging/silent-monitor-start-acknowledged',
        'cxengage/interactions/messaging/silent-monitor-end',
        'cxengage/interactions/messaging/silent-monitor-end-acknowledged',
        'cxengage/interactions/messaging/silent-monitor-message-history',
      ])
        .mergeMap((subscription) =>
          fromPromise(
            sdkPromise({
              module: 'subscribe',
              command: subscription,
              topic: subscription,
            })
          ).mapTo(interactionMonitoringSubscriptionAdded(subscription))
        )
        .mapTo({ topic: 'MONITOR_SUBSCRIPTION_START' })
    );

export const RequestingSessionInitialization = (action$, store) =>
  action$
    .ofType('REQUESTING_MONITOR_SESSION_INITIALIZATION')
    .throttleTime(4000)
    .switchMap(({ interactionId }) =>
      fromPromise(
        sdkPromise({
          module: 'monitorMessaging',
          data: {
            interactionId,
            sessionId: selectSessionId(store.getState()),
          },
          topic: 'monitorMessaging',
        })
      )
        .mapTo(requestingSessionInitializationCompleted(interactionId))
        .catch((error) => handleError(error, store.getState()))
    );

const WaitSessionStart = (action$, store) =>
  action$
    .ofType('cxengage/session/started')
    .timeout(10000)
    .take(1)
    .catch((error) => handleError(error, store.getState()));

export const RequestingMonitorMessagingStart = (action$, store) =>
  action$
    .ofType('REQUESTING_MONITOR_SESSION_INITIALIZATION_COMPLETED_$')
    .map((action) => ({
      ...action,
      sessionIsActive: selectSessionId(store.getState()) !== null,
    }))
    .switchMap((action) => {
      if (action.sessionIsActive) {
        return from([action]);
      } else {
        return zip(WaitSessionStart(action$, store)).mapTo(action);
      }
    })
    .switchMap((action) =>
      fromPromise(
        sdkPromise({
          module: 'interactions.messaging',
          command: 'silentMonitor',
          data: {
            interactionId: action.interactionId,
            channelType: action.channelType,
          },
          topic: 'cxengage/interactions/messaging/silent-monitor-start',
        })
      ).catch((error) => handleError(error, store.getState()))
    );

export const RequestingMonitorMessagingEnd = (action$, store) =>
  action$.ofType('REQUESTING_MONITOR_MESSAGING_END').switchMap((action) =>
    fromPromise(
      sdkPromise({
        module: 'interactions.messaging',
        command: 'stopMonitor',
        data: { interactionId: action.interactionId },
        topic: 'cxengage/interactions/messaging/silent-monitor-end',
      })
    ).catch((error) => handleError(error, store.getState()))
  );
